import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ComposerAttachmentConfig,
  MAX_ATTACHMENT_SIZE_IN_MB,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from '~hooks/useComposerAttachments/composerAttachmentConfigs';
import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
const BYTES_PER_MB = 1024 * 1024;
const MAX_ATTACHMENT_SIZE_IN_BYTES = MAX_ATTACHMENT_SIZE_IN_MB * BYTES_PER_MB;

export type FileReceiver = (files: File[]) => void;

export type ComposerAttachmentStatus = 'uploading' | 'uploaded' | 'error';

export interface ComposerAttachment {
  // Client-generated id; the file is not persisted until the message is sent.
  id: string;
  filename: string;
  contentType: string;
  status: ComposerAttachmentStatus;
  // The direct-upload signed id, set once the S3 upload succeeds.
  signedId: string | null;
  // Object URL for an image preview, revoked on remove/unmount to avoid leaking memory.
  previewUrl: string | null;
  isImage: boolean;
}

type AttachmentError = {
  blocksSend: boolean;
  message: string;
};

function fileExtension(filename: string) {
  const parts = filename.split('.');

  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function isAllowedType(file: File, config: ComposerAttachmentConfig) {
  return (
    config.acceptedContentTypes.includes(file.type) ||
    config.acceptedExtensions.includes(fileExtension(file.name))
  );
}

// Monotonic counter for client-side attachment ids. A counter (not a random id) keeps the
// hook deterministic and side-effect free, which matters for tests.
let nextAttachmentId = 0;

/**
 * Owns a chat composer's pending file attachments: guardrail validation, direct-upload
 * orchestration, image previews, and per-file removal. This is a UI-state hook — the actual
 * server mutation is delegated to useDirectUploads (which lives in the sanctioned data
 * layer), so the composer only ever holds local state and derived signed ids.
 */
export default function useComposerAttachments(config: ComposerAttachmentConfig) {
  const { createDirectUpload } = useDirectUploads();
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const [error, setError] = useState<AttachmentError | null>(null);

  // Kept in a ref so the unmount cleanup revokes the latest set of object URLs without
  // re-running the effect on every attachment change.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const uploadAttachment = useCallback(
    async (attachmentId: string, file: File) => {
      try {
        const directUpload = await createDirectUpload(file);

        setAttachments((previous) =>
          previous.map((attachment) =>
            attachment.id === attachmentId
              ? {
                  ...attachment,
                  status: 'uploaded',
                  signedId: directUpload.signed_id,
                }
              : attachment,
          ),
        );
      } catch {
        // createDirectUpload swallows its own errors, but guard anyway so a failed upload
        // marks the file as errored rather than leaving it stuck spinning.
        setAttachments((previous) =>
          previous.map((attachment) =>
            attachment.id === attachmentId
              ? { ...attachment, status: 'error' }
              : attachment,
          ),
        );
      }
    },
    [createDirectUpload],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      // Paste and drop have no disabled button at the cap, and the strip stays as the sender
      // built it, so this refusal informs without holding the send back.
      if (attachmentsRef.current.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
        setError({
          blocksSend: false,
          message: t(config.errorMessageKeys.tooMany, {
            max: MAX_ATTACHMENTS_PER_MESSAGE,
          }),
        });
        return;
      }

      // Validate and build attachments here in the event handler — never inside the
      // setAttachments updater, which React re-runs (e.g. under StrictMode) and would
      // otherwise double-fire the uploads and object-URL creation below.
      const accepted: { attachment: ComposerAttachment; file: File }[] = [];
      let hasInvalidType = false;
      let hasOversizedFile = false;

      for (const file of files) {
        if (!isAllowedType(file, config)) {
          hasInvalidType = true;
          continue;
        }

        if (file.size > MAX_ATTACHMENT_SIZE_IN_BYTES) {
          hasOversizedFile = true;
          continue;
        }

        const isImage = file.type.startsWith('image/');
        accepted.push({
          attachment: {
            id: `attachment-${(nextAttachmentId += 1)}`,
            filename: file.name,
            contentType: file.type,
            status: 'uploading',
            signedId: null,
            previewUrl: isImage ? URL.createObjectURL(file) : null,
            isImage,
          },
          file,
        });
      }

      if (hasInvalidType) {
        setError({ blocksSend: true, message: t(config.errorMessageKeys.invalidType) });
      } else if (hasOversizedFile) {
        setError({
          blocksSend: true,
          message: t(config.errorMessageKeys.tooLarge, {
            maxSize: MAX_ATTACHMENT_SIZE_IN_MB,
          }),
        });
      } else {
        setError(null);
      }

      if (accepted.length === 0) {
        return;
      }

      const acceptedAttachments = accepted.map((item) => item.attachment);
      // Advance the ref synchronously so a removal fired before the next render sees these;
      // the next render re-syncs it to the committed state.
      attachmentsRef.current = [...attachmentsRef.current, ...acceptedAttachments];
      setAttachments((previous) => [...previous, ...acceptedAttachments]);
      accepted.forEach((item) => void uploadAttachment(item.attachment.id, item.file));
    },
    [config, uploadAttachment],
  );

  // The composer blocks sending while an attachment error stands, so editing the draft has
  // to clear the errors that leave nothing on screen to act on.
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const removeAttachment = useCallback((attachmentId: string) => {
    setError(null);
    const removed = attachmentsRef.current.find(
      (attachment) => attachment.id === attachmentId,
    );

    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }

    // Keep the ref in step with the state so a subsequent add in the same tick builds on the
    // post-removal list.
    attachmentsRef.current = attachmentsRef.current.filter(
      (attachment) => attachment.id !== attachmentId,
    );
    setAttachments((previous) =>
      previous.filter((attachment) => attachment.id !== attachmentId),
    );
  }, []);

  const clearAttachments = useCallback(() => {
    setError(null);
    attachmentsRef.current.forEach((attachment) => {
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    });
    attachmentsRef.current = [];
    setAttachments([]);
  }, []);

  // Revoke any outstanding preview URLs when the composer unmounts.
  useEffect(
    () => () => {
      attachmentsRef.current.forEach((attachment) => {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
    },
    [],
  );

  const isAtAttachmentLimit = attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE;
  // A file picker cannot limit how many files the OS dialog lets you select, so an over-cap
  // selection stays on screen: dropping the extras silently would read as though they were
  // accepted, and the sender is the one who knows which to remove. The composer holds the
  // send back meanwhile, so this outlives a transient error and clears only by removing.
  const isOverAttachmentLimit = attachments.length > MAX_ATTACHMENTS_PER_MESSAGE;
  const displayedError = isOverAttachmentLimit
    ? t(config.errorMessageKeys.tooMany, { max: MAX_ATTACHMENTS_PER_MESSAGE })
    : (error?.message ?? null);
  // A file whose upload failed is still on screen, but its bytes never reached the store, so
  // sending now would quietly drop it.
  const hasFailedAttachment = attachments.some(
    (attachment) => attachment.status === 'error',
  );
  const isUploading = attachments.some((attachment) => attachment.status === 'uploading');
  const hasBlockingError = isOverAttachmentLimit || Boolean(error?.blocksSend);
  const blocksSend = isUploading || hasBlockingError || hasFailedAttachment;
  const uploadedSignedIds = attachments
    .filter((attachment) => attachment.status === 'uploaded' && attachment.signedId)
    .map((attachment) => attachment.signedId as string);

  return {
    addFiles,
    attachments,
    blocksSend,
    clearAttachments,
    clearError,
    error: displayedError,
    isAtAttachmentLimit,
    removeAttachment,
    uploadedSignedIds,
  };
}
